/**
 * Ticketing Service API Contracts
 * Request/Response schemas for the ticketing service
 */

import { z } from 'zod';

import { ApiResponseSchema, BooleanFromString, PaginatedResponseSchema, type SuccessResponse } from '../common';

import {
  BundleItemKeySchema,
  BundleParentAddressingSchema,
  OrderAttractionAttachmentSchema,
} from './attraction-attachment';

// ============================================================================
// Enum Schemas (Ticket Bundling -- Sprint 3)
// ============================================================================

export const TicketCategorySchema = z.enum(['ENTRY', 'ATTRACTION']);
export type TicketCategoryType = z.infer<typeof TicketCategorySchema>;

export const AttractionCategorySchema = z.enum([
  'thrill',
  'family',
  'kids',
  'water',
  'show',
  'experience',
]);
export type AttractionCategoryType = z.infer<typeof AttractionCategorySchema>;

export const AttractionAccessTypeSchema = z.enum(['CREDIT', 'TICKET', 'FREE']);
export type AttractionAccessTypeType = z.infer<typeof AttractionAccessTypeSchema>;

// Gate-side face capture policy (admin: Gate Policy section). SOFT captures a
// face but still admits on failure; REQUIRED blocks entry/exit until a face
// is captured — keep a staffed lane when using REQUIRED.
export const FaceCaptureModeSchema = z.enum(['OFF', 'SOFT', 'REQUIRED']);
export type FaceCaptureModeType = z.infer<typeof FaceCaptureModeSchema>;

// What to do with a captured face after the scan. DELETE (default) discards
// it — one-time verification only. REGISTER persists the enrolment on a
// target device so it can later recognise the visitor (e.g. a face re-entry).
// Only meaningful for type=PARK, and only when the matching faceCapture* mode
// isn't OFF — enforced service-side, not by these schemas.
export const FacePostActionSchema = z.enum(['DELETE', 'REGISTER']);
export type FacePostActionType = z.infer<typeof FacePostActionSchema>;

// Where a REGISTER post-action enrols the captured face: either a specific
// camera device, or one-or-more other attractions' IN/OUT cameras (the union
// of attractionIds × directions).
export const FaceRegisterTargetSchema = z.object({
  mode: z.enum(['DEVICE', 'ATTRACTION']),
  deviceId: z.string().uuid().optional(),
  attractionIds: z.array(z.string().uuid()).optional(),
  directions: z.array(z.enum(['IN', 'OUT'])).optional(),
});
export type FaceRegisterTargetType = z.infer<typeof FaceRegisterTargetSchema>;

/**
 * PARK|ATTRACTION|ZONE discriminator, mirroring the monolith's kind field
 * (PARK is the park itself; ZONE is a sub-park container/grouping; ATTRACTION
 * is the classic per-ride attraction). At most one row may carry kind=PARK —
 * enforced by a partial unique index + a belt-and-braces service-layer guard
 * in AttractionsService. ZONE has no containment/hierarchy support yet (no
 * parentId, no capacityEnforced) — selectable classification only.
 */
export const AttractionKindSchema = z.enum(['PARK', 'ATTRACTION', 'ZONE']);
export type AttractionKindType = z.infer<typeof AttractionKindSchema>;

/**
 * Scan methods allowed for a re-entry (PARK/ZONE only). Empty list = any
 * method. Matches monolith `ReEntryMethod` — see
 * docs/design/features/reentry-methods.md.
 */
export const ReEntryMethodSchema = z.enum(['qr_code', 'face_recognition', 'access_card']);
export type ReEntryMethodType = z.infer<typeof ReEntryMethodSchema>;
export const REENTRY_METHODS = ReEntryMethodSchema.options;

export const AttractionStatusSchema = z.enum([
  'OPERATIONAL',
  'MAINTENANCE',
  'CLOSED',
  'WEATHER_CLOSURE',
  'TECHNICAL_ISSUE',
  'STAFF_SHORTAGE',
]);
export type AttractionStatusType = z.infer<typeof AttractionStatusSchema>;

/**
 * Kiosk-facing attraction-credits listing (GET /attractions/credits, public).
 * Verified against AttractionsService.listAttractionCredits() -- a purpose-built
 * projection, not the full AttractionSchema (no description/height/age/capacity
 * fields are selected).
 */
export const AttractionCreditItemSchema = z.object({
  attractionId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  category: AttractionCategorySchema,
  creditCost: z.number().int().nonnegative(),
  accessType: AttractionAccessTypeSchema,
  status: AttractionStatusSchema,
  thumbnailUrl: z.string().nullable(),
});
export type AttractionCreditItem = z.infer<typeof AttractionCreditItemSchema>;

export const AttractionCreditsResponseSchema = ApiResponseSchema(
  z.array(AttractionCreditItemSchema),
);

/**
 * Show taxonomy — the kind of show/experience an attraction represents.
 *
 * Canonical, single-source definition (CLAUDE.md rule #1). Backed by the
 * `ShowType` Postgres enum + indexed `show_type` column on `Attraction`;
 * imported by ticketing-service, apps/admin, and apps/web. Do NOT redefine
 * these value lists locally — derive option arrays from `.options`.
 */
export const ShowTypeSchema = z.enum(['LIVE_SHOW', 'EXHIBITION', 'WORKSHOP', 'PERFORMANCE']);
export type ShowTypeType = z.infer<typeof ShowTypeSchema>;

/**
 * Audience classification for a show/experience.
 *
 * Canonical, single-source definition (CLAUDE.md rule #1). Backed by the
 * `Classification` Postgres enum + indexed `classification` column on
 * `Attraction`; imported by ticketing-service, apps/admin, and apps/web.
 * Do NOT redefine these value lists locally — derive option arrays from `.options`.
 */
export const ClassificationSchema = z.enum(['FAMILY', 'ADULTS', 'KIDS', 'GENERAL']);
export type ClassificationType = z.infer<typeof ClassificationSchema>;

export const BundlePricingStrategySchema = z.enum(['FIXED', 'DISCOUNT_ON_SUM']);
export type BundlePricingStrategyType = z.infer<typeof BundlePricingStrategySchema>;

export const TicketStatusSchema = z.enum([
  'PENDING',
  'ACTIVE',
  'USED',
  'EXPIRED',
  'CANCELLED',
  'REFUNDED',
  'VOIDED',
]);
export type TicketStatusType = z.infer<typeof TicketStatusSchema>;

// ============================================================================
// Ticket Types
// ============================================================================

export const TicketTypeSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  category: TicketCategorySchema,
  basePriceIdr: z.number().int(),
  childPricePercentage: z.number().int(),
  seniorPricePercentage: z.number().int(),
  minAge: z.number().int().nullable(),
  maxAge: z.number().int().nullable(),
  validityDays: z.number().int(),
  maxReentryCount: z.number().int(),
  ticketDurationType: z.string().optional(),
  maxUseDays: z.number().int().nullable().optional(),
  validityWindowDays: z.number().int().nullable().optional(),
  includesAttractions: z.boolean(),
  attractionCredits: z.number().int(),
  isBlackoutExempt: z.boolean(),
  requiresPhoto: z.boolean(),
  requiresIdVerification: z.boolean(),
  isActive: z.boolean(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  standalonePurchasable: z.boolean().default(true),
  attractionId: z.string().uuid().nullable().default(null),
  imageUrl: z.string().nullable().default(null),
  resolvedPriceIdr: z.number().int().nullable().optional(),
  // Scheduling / time-slot fields (monolith products.* parity — consumed by
  // apps/tiket-online to fetch /ticket-schedule/{scheduleId}/availability/{date})
  scheduleId: z.string().nullable().optional(),
  requiresTimeSlot: z.boolean().nullable().optional(),
  slotDurationMinutes: z.number().nullable().optional(),
  defaultCapacityPerSlot: z.number().nullable().optional(),
  presaleWindowDays: z.number().nullable().optional(),
  timezone: z.string().nullable().optional(),
  pricingScheduleId: z.string().nullable().optional(),
});

export type TicketType = z.infer<typeof TicketTypeSchema>;

export const GetTicketTypesQuerySchema = z.object({
  category: TicketCategorySchema.optional(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  includeInactive: BooleanFromString.default(false),
});

export type GetTicketTypesQuery = z.infer<typeof GetTicketTypesQuerySchema>;

/** Metadata on GET /tickets/types list responses */
export const TicketTypesListMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  cached: z.boolean(),
  cachedAt: z.string().optional(),
});

export type TicketTypesListMeta = z.infer<typeof TicketTypesListMetaSchema>;

/** Full HTTP body for GET /tickets/types */
export const TicketTypesListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(TicketTypeSchema),
  meta: TicketTypesListMetaSchema,
});

export type TicketTypesListResponse = z.infer<typeof TicketTypesListResponseSchema>;

export const TicketAvailabilitySchema = z.object({
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  visitDate: z.string(),
  totalCapacity: z.number().int(),
  availableOnline: z.number().int(),
  soldOnline: z.number().int(),
  reserved: z.number().int(),
  priceIdr: z.number().int(),
  basePriceIdr: z.number().int(),
  priceMultiplier: z.number(),
  isBlocked: z.boolean(),
  blockReason: z.string().nullable(),
  status: z.enum(['available', 'limited', 'sold_out', 'blocked']),
  capacityPercentage: z.number().int(),
});

export type TicketAvailability = z.infer<typeof TicketAvailabilitySchema>;

export const GetAvailabilityQuerySchema = z.object({
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  ticketTypeCode: z.string().optional(),
});

export type GetAvailabilityQuery = z.infer<typeof GetAvailabilityQuerySchema>;

// ============================================================================
// Attractions (New -- Ticket Bundling Sprint 3)
// ============================================================================

export const AttractionSchema = z.object({
  id: z.string().uuid(),
  code: z.string().max(50),
  name: z.string().max(100),
  nameId: z.string().max(100),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  // Only meaningful for type=ATTRACTION — PARK/ZONE rows never carry one.
  category: AttractionCategorySchema.nullable(),
  type: AttractionKindSchema,
  // Self-reference to another Attraction's id (must be type=ZONE, enforced
  // service-side). Null means "directly in the park" — PARK rows never
  // carry one. Mirrors the monolith's `parentId`.
  zoneId: z.string().uuid().nullable(),
  // Free-text human-readable location description (e.g. "Near the north
  // gate"). Distinct from zoneId, which is a structured Zone reference.
  location: z.string().max(200).nullable(),
  creditCost: z.number().int().nonnegative(),
  accessType: AttractionAccessTypeSchema,
  minHeightCm: z.number().int().nullable(),
  maxHeightCm: z.number().int().nullable(),
  minAge: z.number().int().nullable(),
  maxAge: z.number().int().nullable(),
  capacityPerCycle: z.number().int().nonnegative(),
  cycleDurationMinutes: z.number().int().nonnegative(),
  // Aggregate ceiling per materialised slot (distinct from capacityPerCycle,
  // the physical headcount). Null when isUnlimited (sells without slot
  // reservations) or when it inherits from the linked product.
  defaultCapacityPerSlot: z.number().int().nonnegative().nullable(),
  isUnlimited: z.boolean(),
  // How many minutes outside a booked slot's window a scan is still admitted
  // (validation-service enforcement). -1 = inherit the park-wide default
  // (validation.attractionEntryWindowMinutes); 0 = no grace; 1-1440 = an
  // explicit override in minutes.
  scanGraceMinutes: z.number().int().min(-1).max(1440),
  // Purchase-limit enforcement: max tickets one visitor (wristband) may hold
  // for a single time slot here. Null = no limit.
  maxTicketsPerSlot: z.number().int().positive().nullable(),
  // Mutually exclusive with maxTicketsPerSlot — only meaningful when NOT a
  // ticketed ATTRACTION. -1 = unlimited re-entry; 0 = single entry (default);
  // N = N re-entries (N+1 total visits).
  reEntryLimit: z.number().int().min(-1),
  faceCaptureEntry: FaceCaptureModeSchema,
  faceCaptureExit: FaceCaptureModeSchema,
  // Only meaningful for type=PARK, and only when the matching faceCapture*
  // isn't OFF — ATTRACTION/ZONE rows never carry one.
  facePostActionIn: FacePostActionSchema,
  facePostActionOut: FacePostActionSchema,
  faceRegisterTargetIn: FaceRegisterTargetSchema.nullable().optional(),
  faceRegisterTargetOut: FaceRegisterTargetSchema.nullable().optional(),
  // Only meaningful for type=PARK/ZONE — ATTRACTION rows always [].
  // Empty = unrestricted; non-empty = only listed methods may re-enter.
  reEntryMethods: z.array(ReEntryMethodSchema),
  // The ticket schedule driving this attraction's calendar. Null when no
  // schedule is attached yet (materialiser falls back to cycleDurationMinutes).
  scheduleId: z.string().uuid().nullable(),
  operatingHoursStart: z.string().nullable(),
  operatingHoursEnd: z.string().nullable(),
  status: AttractionStatusSchema,
  pointsEarned: z.number().int().nonnegative(),
  thumbnailUrl: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  galleryUrls: z.array(z.string()),
  showType: ShowTypeSchema.nullable(),
  classification: ClassificationSchema.nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AttractionType = z.infer<typeof AttractionSchema>;

// Admin detail = base DTO + relation counts (+ optional ticket types). Mirrors
// `mapToDetailDto` in attractions.service.ts.
export const AttractionDetailSchema = AttractionSchema.extend({
  _counts: z
    .object({
      ticketTypes: z.number().int(),
      schedules: z.number().int(),
      dailyCapacities: z.number().int(),
      tickets: z.number().int(),
    })
    .optional(),
  ticketTypes: z
    .array(
      z.object({
        id: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        basePriceIdr: z.number().int(),
        isActive: z.boolean(),
      }),
    )
    .optional(),
});

export type AttractionDetail = z.infer<typeof AttractionDetailSchema>;

// ---------------------------------------------------------------------------
// Attraction availability (GET /attractions/availability) + capacity time slots
// ---------------------------------------------------------------------------

export const AttractionAvailabilityStatusSchema = z.enum([
  'AVAILABLE',
  'LIMITED',
  'SOLD_OUT',
  'CLOSED',
]);
export type AttractionAvailabilityStatus = z.infer<typeof AttractionAvailabilityStatusSchema>;

export const AttractionAvailabilityItemSchema = z.object({
  attractionId: z.string().nullable(),
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  ticketTypeNameId: z.string(),
  visitDate: z.string(),
  available: z.number().int(),
  totalCapacity: z.number().int(),
  status: AttractionAvailabilityStatusSchema,
  remainingSlotsForGroupSize: z.number().int().nullable(),
  isBlocked: z.boolean(),
  message: z.string().nullable(),
});
export type AttractionAvailabilityItem = z.infer<typeof AttractionAvailabilityItemSchema>;

/**
 * Today's calendar day in Asia/Jakarta (WIB, UTC+7), as `YYYY-MM-DD`.
 *
 * Deliberately dependency-free: this package is consumed by the browser bundles as well
 * as by the services, so it cannot reach for a Node-side date library. `en-CA` formats as
 * `YYYY-MM-DD`, the shape every date field in these contracts uses.
 */
const JAKARTA_TODAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Jakarta',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const todayInJakarta = (): string => JAKARTA_TODAY.format(new Date());

export const AttractionAvailabilityQuerySchema = z.object({
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  groupSize: z.coerce.number().int().positive().optional(),
}).refine(
  (data) => {
    // The park's day, not UTC's. `toISOString()` renders UTC, and Autopia operates in
    // Bandung (WIB, UTC+7), so for the first seven hours of every operating day the UTC
    // date is YESTERDAY — and this rule would then admit a visit date genuinely past.
    return data.visitDate >= todayInJakarta();
  },
  { message: 'Cannot check availability for past dates', path: ['visitDate'] },
);
export type AttractionAvailabilityQuery = z.infer<typeof AttractionAvailabilityQuerySchema>;

// NOTE: per-slot capacity types (`AttractionTimeSlot`, `AttractionTimeSlotStatus`)
// live in the capacity contract (`../capacity`) — that endpoint is served by
// capacity-service. Import them from there; not redefined here.

// ---------------------------------------------------------------------------
// Attractions — admin list envelope + schedule/timeslot operations
// ---------------------------------------------------------------------------

export const AttractionsResponseSchema = z.object({
  attractions: z.array(AttractionSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type AttractionsResponse = z.infer<typeof AttractionsResponseSchema>;

export const AttractionsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  isActive: BooleanFromString.optional(),
  category: AttractionCategorySchema.optional(),
  status: AttractionStatusSchema.optional(),
  showType: ShowTypeSchema.optional(),
  classification: ClassificationSchema.optional(),
  zoneId: z.string().uuid().optional(),
  search: z.string().optional(),
});
export type AttractionsQuery = z.infer<typeof AttractionsQuerySchema>;

export const AttractionScheduleDaySchema = z.object({
  date: z.string(),
  operatingStart: z.string().nullable(),
  operatingEnd: z.string().nullable(),
  isClosed: z.boolean(),
  closeReason: z.string().nullable(),
  hasOverride: z.boolean(),
  totalSlots: z.number().int(),
  totalReserved: z.number().int(),
});
export type AttractionScheduleDay = z.infer<typeof AttractionScheduleDaySchema>;

export const AttractionScheduleCalendarSchema = z.object({
  attraction: z.object({
    id: z.string().uuid(),
    name: z.string(),
    operatingStart: z.string().nullable(),
    operatingEnd: z.string().nullable(),
    capacityPerCycle: z.number().int(),
    cycleDurationMin: z.number().int(),
  }),
  days: z.array(AttractionScheduleDaySchema),
});
export type AttractionScheduleCalendar = z.infer<typeof AttractionScheduleCalendarSchema>;

export const AttractionTimeSlotAdminSchema = z.object({
  id: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  capacity: z.number().int(),
  reserved: z.number().int(),
  available: z.number().int(),
  isActive: z.boolean(),
});
export type AttractionTimeSlotAdmin = z.infer<typeof AttractionTimeSlotAdminSchema>;

export const UpsertScheduleOverrideRequestSchema = z.object({
  operatingStart: z.string().optional(),
  operatingEnd: z.string().optional(),
  isClosed: z.boolean().optional(),
  closeReason: z.string().optional(),
});
export type UpsertScheduleOverrideRequest = z.infer<typeof UpsertScheduleOverrideRequestSchema>;

export const BulkGenerateTimeslotsRequestSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  slotDurationMinutes: z.number().int().optional(),
  capacityPerSlot: z.number().int().optional(),
});
export type BulkGenerateTimeslotsRequest = z.infer<typeof BulkGenerateTimeslotsRequestSchema>;

export const BulkGenerateTimeslotsResponseSchema = z.object({
  datesProcessed: z.number().int(),
  slotsCreated: z.number().int(),
  datesSkipped: z.number().int(),
});
export type BulkGenerateTimeslotsResponse = z.infer<typeof BulkGenerateTimeslotsResponseSchema>;

export const ScheduleOverrideResponseSchema = z.object({
  id: z.string().uuid(),
  attractionId: z.string().uuid(),
  scheduleDate: z.string(),
  operatingStart: z.string().nullable(),
  operatingEnd: z.string().nullable(),
  isClosed: z.boolean(),
  closeReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ScheduleOverrideResponse = z.infer<typeof ScheduleOverrideResponseSchema>;

export const CloseAttractionDateResponseSchema = z.object({
  id: z.string().uuid(),
  attractionId: z.string().uuid(),
  scheduleDate: z.string(),
  isClosed: z.boolean(),
  closeReason: z.string().nullable(),
  timeslotsDeactivated: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CloseAttractionDateResponse = z.infer<typeof CloseAttractionDateResponseSchema>;

export const BulkGenerateTimeslotsFullResponseSchema = z.object({
  attractionId: z.string().uuid(),
  datesProcessed: z.number().int(),
  slotsCreated: z.number().int(),
  datesSkipped: z.array(z.object({ date: z.string(), reason: z.string() })),
  configuration: z.object({
    slotDurationMinutes: z.number().int(),
    capacityPerSlot: z.number().int(),
  }),
});
export type BulkGenerateTimeslotsFullResponse = z.infer<typeof BulkGenerateTimeslotsFullResponseSchema>;

export const DeleteTimeslotsResponseSchema = z.object({
  deletedCount: z.number().int(),
});
export type DeleteTimeslotsResponse = z.infer<typeof DeleteTimeslotsResponseSchema>;

export const MaterializeSlotsResponseSchema = z.object({
  slots: z.array(z.unknown()),
  upserted: z.number().int().optional(),
  skipped: z.number().int().optional(),
  dryRun: z.boolean(),
});
export type MaterializeSlotsResponse = z.infer<typeof MaterializeSlotsResponseSchema>;

export const TimeslotListItemSchema = AttractionTimeSlotAdminSchema.extend({
  attractionId: z.string().uuid(),
  createdAt: z.string().optional(),
});
export type TimeslotListItem = z.infer<typeof TimeslotListItemSchema>;

export const RegenerateScheduleSlotsQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dryRun: BooleanFromString.default(false),
});
export type RegenerateScheduleSlotsQuery = z.infer<typeof RegenerateScheduleSlotsQuerySchema>;

const CreateAttractionFieldsSchema = z.object({
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/),
  name: z.string().min(1).max(100),
  nameId: z.string().min(1).max(100),
  description: z.string().max(1000).optional(),
  descriptionId: z.string().max(1000).optional(),
  // Only meaningful for type=ATTRACTION — enforced below via superRefine
  // rather than here, since it depends on the (also optional) type field.
  category: AttractionCategorySchema.optional(),
  // Admin-only escalation to the park singleton — omit to default to
  // ATTRACTION. Promoting a row to PARK is audit-logged service-side.
  type: AttractionKindSchema.optional(),
  // Self-reference to another Attraction's id (must be type=ZONE, enforced
  // service-side). Omit for "directly in the park" — PARK rows never carry
  // one. Mirrors the monolith's `parentId`.
  zoneId: z.string().uuid().optional(),
  location: z.string().max(200).optional(),
  creditCost: z.number().int().nonnegative().default(0),
  accessType: AttractionAccessTypeSchema.default('TICKET'),
  minHeightCm: z.number().int().positive().optional(),
  maxHeightCm: z.number().int().positive().optional(),
  minAge: z.number().int().nonnegative().optional(),
  maxAge: z.number().int().positive().optional(),
  capacityPerCycle: z.number().int().positive().default(1),
  cycleDurationMinutes: z.number().int().positive().default(5),
  defaultCapacityPerSlot: z.number().int().nonnegative().optional(),
  isUnlimited: z.boolean().default(false),
  scanGraceMinutes: z.number().int().min(-1).max(1440).default(-1),
  maxTicketsPerSlot: z.number().int().positive().optional(),
  reEntryLimit: z.number().int().min(-1).default(0),
  faceCaptureEntry: FaceCaptureModeSchema.default('OFF'),
  faceCaptureExit: FaceCaptureModeSchema.default('OFF'),
  facePostActionIn: FacePostActionSchema.default('DELETE'),
  facePostActionOut: FacePostActionSchema.default('DELETE'),
  faceRegisterTargetIn: FaceRegisterTargetSchema.optional(),
  faceRegisterTargetOut: FaceRegisterTargetSchema.optional(),
  reEntryMethods: z.array(ReEntryMethodSchema).default([]),
  scheduleId: z.string().uuid().optional(),
  operatingHoursStart: z.string().regex(/^\d{2}:\d{2}$/).default('09:00'),
  operatingHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).default('21:00'),
  pointsEarned: z.number().int().nonnegative().default(10),
  thumbnailUrl: z.string().url().max(500).optional(),
  coverImageUrl: z.string().url().max(500).optional(),
  galleryUrls: z.array(z.string().url()).max(10).optional(),
  showType: ShowTypeSchema.optional(),
  classification: ClassificationSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
  status: AttractionStatusSchema.optional(),
  sortOrder: z.number().int().nonnegative().default(0),
  isActive: z.boolean().default(true),
});

export const CreateAttractionSchema = CreateAttractionFieldsSchema.superRefine((data, ctx) => {
  const effectiveType = data.type ?? 'ATTRACTION';
  if (effectiveType === 'ATTRACTION' && !data.category) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'category is required when type is ATTRACTION',
      path: ['category'],
    });
  }
});

export type CreateAttractionInput = z.infer<typeof CreateAttractionSchema>;

export const UpdateAttractionSchema = z.object({
  status: AttractionStatusSchema.optional(),
  name: z.string().min(1).max(100).optional(),
  nameId: z.string().min(1).max(100).optional(),
  description: z.string().optional(),
  descriptionId: z.string().optional(),
  // Nullable (not just optional): demoting to PARK/ZONE clears category back
  // to null — omitting means "leave unchanged" (same pattern as zoneId).
  category: AttractionCategorySchema.nullable().optional(),
  type: AttractionKindSchema.optional(),
  // Nullable (not just optional): clearing zoneId back to "directly in the
  // park" needs an explicit null — omitting means "leave unchanged" (same
  // pattern as scheduleId below).
  zoneId: z.string().uuid().nullable().optional(),
  location: z.string().max(200).nullable().optional(),
  creditCost: z.number().int().nonnegative().optional(),
  accessType: AttractionAccessTypeSchema.optional(),
  minHeightCm: z.number().int().positive().nullable().optional(),
  maxHeightCm: z.number().int().positive().nullable().optional(),
  minAge: z.number().int().nonnegative().nullable().optional(),
  maxAge: z.number().int().positive().nullable().optional(),
  capacityPerCycle: z.number().int().positive().optional(),
  cycleDurationMinutes: z.number().int().positive().optional(),
  defaultCapacityPerSlot: z.number().int().nonnegative().nullable().optional(),
  isUnlimited: z.boolean().optional(),
  scanGraceMinutes: z.number().int().min(-1).max(1440).optional(),
  maxTicketsPerSlot: z.number().int().positive().nullable().optional(),
  reEntryLimit: z.number().int().min(-1).optional(),
  faceCaptureEntry: FaceCaptureModeSchema.optional(),
  faceCaptureExit: FaceCaptureModeSchema.optional(),
  facePostActionIn: FacePostActionSchema.optional(),
  facePostActionOut: FacePostActionSchema.optional(),
  faceRegisterTargetIn: FaceRegisterTargetSchema.nullable().optional(),
  faceRegisterTargetOut: FaceRegisterTargetSchema.nullable().optional(),
  reEntryMethods: z.array(ReEntryMethodSchema).optional(),
  // Nullable (not just optional): clearing scheduleId back to unattached
  // needs an explicit null — omitting means "leave unchanged" (#743, same
  // pattern as UpdateDeviceRequestSchema.attractionId).
  scheduleId: z.string().uuid().nullable().optional(),
  operatingHoursStart: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  operatingHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  pointsEarned: z.number().int().nonnegative().optional(),
  thumbnailUrl: z.string().url().max(500).nullable().optional(),
  coverImageUrl: z.string().url().max(500).nullable().optional(),
  galleryUrls: z.array(z.string().url()).max(10).nullable().optional(),
  showType: ShowTypeSchema.nullable().optional(),
  classification: ClassificationSchema.nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

export type UpdateAttractionInput = z.infer<typeof UpdateAttractionSchema>;

// Shows are Attractions with category='show'. These schemas omit fields that
// are not part of the show management UI (height/age restrictions, gallery,
// metadata), the category itself (always injected as 'show' by the hook),
// and type (a show can never be the PARK singleton).
const _SHOW_OMIT = {
  category: true,
  type: true,
  minHeightCm: true,
  maxHeightCm: true,
  minAge: true,
  maxAge: true,
  galleryUrls: true,
  metadata: true,
  reEntryMethods: true,
  facePostActionIn: true,
  facePostActionOut: true,
  faceRegisterTargetIn: true,
  faceRegisterTargetOut: true,
} as const;

export const CreateShowSchema = CreateAttractionFieldsSchema.omit(_SHOW_OMIT);
export type CreateShowInput = z.infer<typeof CreateShowSchema>;

export const UpdateShowSchema = UpdateAttractionSchema.omit({ ..._SHOW_OMIT, status: true });
export type UpdateShowInput = z.infer<typeof UpdateShowSchema>;

export const GetAttractionsQuerySchema = z.object({
  category: AttractionCategorySchema.optional(),
  zoneId: z.string().uuid().optional(),
  status: AttractionStatusSchema.optional(),
  accessType: AttractionAccessTypeSchema.optional(),
  showType: ShowTypeSchema.optional(),
  classification: ClassificationSchema.optional(),
  isActive: BooleanFromString.optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

export type GetAttractionsQuery = z.infer<typeof GetAttractionsQuerySchema>;

/**
 * Lightweight row for admin picker/dropdown UIs (zone parent picker, face
 * post-action register-target picker, etc.) — the full AttractionSchema is
 * ~40 fields; these callers only ever read id/name/code/status/type. Mirrors
 * the monolith's `GET /admin/attraction/options` shape.
 */
export const AttractionOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  status: AttractionStatusSchema,
  type: AttractionKindSchema,
});
export type AttractionOption = z.infer<typeof AttractionOptionSchema>;

export const GetAttractionOptionsQuerySchema = z.object({
  // Unset = every kind (needed by pickers like the face-register-target
  // list, which offer PARK/ATTRACTION/ZONE alike).
  type: AttractionKindSchema.optional(),
});
export type GetAttractionOptionsQuery = z.infer<typeof GetAttractionOptionsQuerySchema>;

export const MaterializeAttractionQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dryRun: BooleanFromString.default(false),
});
export type MaterializeAttractionQuery = z.infer<typeof MaterializeAttractionQuerySchema>;

// ============================================================================
// Ticket Bundles (New -- Ticket Bundling Sprint 3)
// ============================================================================

export const BundleItemSchema = z.object({
  id: z.string().uuid(),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  ticketTypeNameId: z.string(),
  category: TicketCategorySchema,
  /** Stable, bundle-scoped line id. See BundleItemKeySchema. */
  itemKey: BundleItemKeySchema,
  /** itemKey of the owning ENTRY line; null on ENTRY lines. */
  parentItemKey: BundleItemKeySchema.nullable(),
  /**
   * PER ENTRY UNIT on ATTRACTION lines — redemptions for one visitor, not a
   * line total. Read `effectiveQuantity` instead when you need the number of
   * units the line contributes to pricing or inventory.
   */
  quantity: z.number().int().positive(),
  /**
   * Server-derived total units this line contributes:
   * `parentItemKey ? quantity * parent.quantity : quantity`. Provided so no
   * client has to re-implement the per-entry-unit rule.
   */
  effectiveQuantity: z.number().int().positive(),
  unitPriceIdr: z.number().int().nonnegative(),
});

export type BundleItemType = z.infer<typeof BundleItemSchema>;

export const TicketBundleSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  bundlePriceIdr: z.number().int().positive(),
  pricingStrategy: BundlePricingStrategySchema,
  discountPercentage: z.number().int().nullable(),
  savingsIdr: z.number().int().nonnegative(),
  savingsPercentage: z.number().nonnegative(),
  thumbnailUrl: z.string().nullable(),
  sortOrder: z.number().int(),
  maxQuantityPerOrder: z.number().int().min(1).max(20).default(5),
  isActive: z.boolean(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  items: z.array(BundleItemSchema),
});

export type TicketBundleType = z.infer<typeof TicketBundleSchema>;

export const CreateBundleItemInputSchema = z.object({
  ticketTypeId: z.string().uuid(),
  /** Stable, bundle-scoped line id. Client-supplied; must be unique per bundle. */
  itemKey: BundleItemKeySchema,
  /**
   * itemKey of the ENTRY line that owns this line. Required on ATTRACTION lines,
   * must be absent on ENTRY lines. Category is not part of this payload, so that
   * pairing can only be checked server-side; the structural rules below cover
   * what is checkable without the DB.
   */
  parentItemKey: BundleItemKeySchema.optional(),
  /**
   * PER ENTRY UNIT on ATTRACTION lines — redemptions for one visitor. Entry
   * Adult x2 owning Water Slide x2 mints 2 adult wristbands, each carrying one
   * Water Slide ticket with maxReentryCount 2.
   */
  quantity: z.number().int().positive().max(10),
});

/**
 * Structural composition rules that need no database access: unique keys, no
 * self-parenting, and every named parent present in the same payload.
 *
 * The category-dependent rules — entry lines are roots, attraction lines are
 * owned, parents are ENTRY — deliberately live server-side in
 * `assertValidComposition`, because the client never sends `category`.
 */
export function refineBundleItemStructure(
  // Deliberately tolerant of missing keys: Zod runs superRefine on the parsed
  // object, but callers type it from `z.input`, where every field is still
  // optional. A missing itemKey is already reported by the field schema, so
  // this pass simply skips those entries rather than duplicating the error.
  items: Array<{ itemKey?: string; parentItemKey?: string }>,
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  items.forEach((item, i) => {
    if (item.itemKey === undefined) return;
    if (seen.has(item.itemKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', i, 'itemKey'],
        message: `Duplicate itemKey "${item.itemKey}"`,
      });
    }
    seen.add(item.itemKey);
  });

  items.forEach((item, i) => {
    if (item.parentItemKey === undefined) return;
    if (item.parentItemKey === item.itemKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', i, 'parentItemKey'],
        message: 'A bundle line cannot be its own parent',
      });
      return;
    }
    if (!seen.has(item.parentItemKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items', i, 'parentItemKey'],
        message: `parentItemKey "${item.parentItemKey}" does not match any itemKey in this bundle`,
      });
    }
  });
}

export type CreateBundleItemInput = z.infer<typeof CreateBundleItemInputSchema>;

export const CreateBundleBaseSchema = z.object({
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/),
  name: z.string().min(1).max(100),
  nameId: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  descriptionId: z.string().max(500).optional(),
  bundlePriceIdr: z.number().int().positive().optional(),
  pricingStrategy: BundlePricingStrategySchema.default('FIXED'),
  discountPercentage: z.number().int().min(1).max(50).optional(),
  thumbnailUrl: z.string().url().max(500).optional(),
  sortOrder: z.number().int().min(0).default(0),
  maxQuantityPerOrder: z.number().int().min(1).max(20).optional().default(5),
  isActive: z.boolean().default(true),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  items: z.array(CreateBundleItemInputSchema).min(2),
  metadata: z.record(z.unknown()).optional(),
});

export const CreateBundleSchema = CreateBundleBaseSchema.superRefine((data, ctx) => {
  if (data.pricingStrategy === 'FIXED' && !data.bundlePriceIdr) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bundlePriceIdr'], message: 'bundlePriceIdr is required for FIXED pricing strategy' });
  }
  if (data.pricingStrategy === 'DISCOUNT_ON_SUM' && !data.discountPercentage) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountPercentage'], message: 'discountPercentage is required for DISCOUNT_ON_SUM pricing strategy' });
  }
  refineBundleItemStructure(data.items, ctx);
});

export type CreateBundleInput = z.input<typeof CreateBundleSchema>;

export const UpdateBundleSchema = z.object({
  code: z.string().min(1).max(50).optional(),
  name: z.string().min(1).max(100).optional(),
  nameId: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  descriptionId: z.string().max(500).nullable().optional(),
  bundlePriceIdr: z.number().int().positive().optional(),
  pricingStrategy: BundlePricingStrategySchema.optional(),
  discountPercentage: z.number().int().min(1).max(50).nullable().optional(),
  thumbnailUrl: z.string().url().nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  maxQuantityPerOrder: z.number().int().min(1).max(20).optional(),
  isActive: z.boolean().optional(),
  validFrom: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export type UpdateBundleInput = z.infer<typeof UpdateBundleSchema>;

export const GetBundlesQuerySchema = z.object({
  isActive: BooleanFromString.optional(),
  pricingStrategy: BundlePricingStrategySchema.optional(),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
});

export type GetBundlesQuery = z.infer<typeof GetBundlesQuerySchema>;

/**
 * @deprecated Alias of {@link GetBundlesQuerySchema}, which is what the bundles
 * controller actually validates against. This was a byte-identical second
 * declaration; kept as an alias so the two cannot drift.
 */
export const BundlesQuerySchema = GetBundlesQuerySchema;

export const AdminBundleQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  search: z.string().optional(),
  sortBy: z.enum(['name', 'code', 'sortOrder', 'createdAt', 'bundlePriceIdr']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type AdminBundleQuery = z.infer<typeof AdminBundleQuerySchema>;

export const AddBundleItemRequestSchema = z.object({
  ticketTypeId: z.string().uuid(),
  /** Stable line id, unique within the bundle. */
  itemKey: BundleItemKeySchema,
  /** Required when adding an ATTRACTION line; must be absent on an ENTRY line. */
  parentItemKey: BundleItemKeySchema.optional(),
  quantity: z.number().int().positive(),
});
export type AddBundleItemRequest = z.infer<typeof AddBundleItemRequestSchema>;

export const AddBundleToCartSchema = z.object({
  bundleId: z.string().uuid(),
  quantity: z.number().int().positive().max(20),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type AddBundleToCartInput = z.infer<typeof AddBundleToCartSchema>;

export const BundleAvailabilitySchema = z.object({
  bundleId: z.string().uuid(),
  bundleCode: z.string(),
  visitDate: z.string(),
  available: z.number().int().nonnegative(),
  limitingTicketTypeCode: z.string(),
  isBlocked: z.boolean(),
});

export type BundleAvailabilityType = z.infer<typeof BundleAvailabilitySchema>;

// ============================================================================
// Cart
// ============================================================================

export const VisitorDetailsSchema = z.object({
  name: z.string().max(100).optional(),
  email: z.string().email().optional(),
  phone: z.string().max(20).optional(),
  dateOfBirth: z.string().optional(),
});

export type VisitorDetails = z.infer<typeof VisitorDetailsSchema>;

export const CartItemSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: TicketCategorySchema,
  quantity: z.number().int().positive(),
  unitPriceIdr: z.number().int(),
  totalPriceIdr: z.number().int(),
  visitDate: z.string(),
  visitorDetails: z.array(VisitorDetailsSchema).optional(),
});

export type CartItem = z.infer<typeof CartItemSchema>;

export const CartBundleItemDetailSchema = z.object({
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: TicketCategorySchema,
  itemKey: BundleItemKeySchema,
  parentItemKey: BundleItemKeySchema.nullable(),
  /** PER ENTRY UNIT on attraction lines — see BundleItemSchema.quantity. */
  quantity: z.number().int().positive(),
  effectiveQuantity: z.number().int().positive(),
  unitPriceIdr: z.number().int(),
});

export type CartBundleItemDetail = z.infer<typeof CartBundleItemDetailSchema>;

export const CartBundleEntrySchema = z.object({
  id: z.string(),
  bundleId: z.string().uuid(),
  bundleCode: z.string(),
  bundleName: z.string(),
  bundlePriceIdr: z.number().int().positive(),
  quantity: z.number().int().positive(),
  totalPriceIdr: z.number().int(),
  visitDate: z.string(),
  items: z.array(CartBundleItemDetailSchema),
});

export type CartBundleEntry = z.infer<typeof CartBundleEntrySchema>;

export const CartAttractionItemSchema = z.object({
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: z.literal('ATTRACTION'),
  unitPriceIdr: z.number().int(),
  quantity: z.number().int().positive(),
  slotId: z.string().optional(),
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
  fromBundleInclusion: z.boolean().optional(),
  /**
   * Which bundle line this row came from, when `fromBundleInclusion` is true.
   * A bundle may now hold two lines of the same ticket type, so ticketTypeId is
   * no longer a unique address within a bundle and must not be used as one.
   */
  bundleItemKey: BundleItemKeySchema.optional(),
  /**
   * Which visitor of the owning ENTRY line this row's slot belongs to (0-based).
   *
   * Item-level, not envelope-level, on purpose: the cart allows exactly ONE
   * attachment per (parentType, parentTemplateId, instanceIndex), so a bundle
   * instance's visitors cannot each have their own envelope. One envelope
   * carries every visitor's slot choice, distinguished by this index.
   */
  parentEntryUnitIndex: z.number().int().nonnegative().optional(),
  /**
   * Which ENTRY line of the bundle this row belongs to.
   *
   * Item-level for the same reason as parentEntryUnitIndex: one attachment per
   * bundle copy is all the cart allows, but a copy admits several visitors and
   * each may choose different add-ons, so the visitor is named per row.
   */
  parentEntryItemKey: BundleItemKeySchema.optional(),
});

export type CartAttractionItem = z.infer<typeof CartAttractionItemSchema>;

export const CartAttractionAttachmentSchema = z
  .object({
    id: z.string(),
    parentType: z.enum(['entry', 'bundle']),
    parentTemplateId: z.string(),
    instanceIndex: z.number().int().nonnegative(),
    instanceLabel: z.string(),
    attractions: z.array(CartAttractionItemSchema),
  })
  .merge(BundleParentAddressingSchema);

export type CartAttractionAttachment = z.infer<typeof CartAttractionAttachmentSchema>;

export const CartSchema = z.object({
  sessionId: z.string(),
  // Optional (not just nullable): the backend omits these keys entirely for a
  // guest cart with no bound customer / no applied coupon rather than sending
  // an explicit null, and JSON drops `undefined`-valued keys on the wire.
  //
  // No `.max(50)`: #886 widened `CartReservation.customerId` to TEXT, and a
  // cap declared here as well is a second declaration of the same rule, free to
  // diverge from the column — the pattern that produced two disagreeing copies
  // of the walk-up formula in the first place.
  customerId: z.string().min(1).nullable().optional(),
  visitDate: z.string().optional(),
  items: z.array(CartItemSchema),
  bundles: z.array(CartBundleEntrySchema).default([]),
  attractionAttachments: z.array(CartAttractionAttachmentSchema).default([]),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  couponCode: z.string().nullable().optional(),
  expiresAt: z.string().datetime(),
  serverTimeIso: z.string().datetime(),
  reservationIds: z.array(z.string()).default([]),
  bundleReservationIds: z.record(z.string(), z.array(z.string())).optional(),
  attachmentReservationIds: z.record(z.string(), z.array(z.string())).optional(),
});

export type Cart = z.infer<typeof CartSchema>;

export const UnavailableCartItemSchema = z.object({
  ticketTypeId: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  reason: z.enum(['sold_out', 'blocked', 'date_passed', 'inactive']),
  availableCount: z.number().int(),
  requestedCount: z.number().int(),
});

export type UnavailableCartItem = z.infer<typeof UnavailableCartItemSchema>;

export const CartValidationResultSchema = z.object({
  valid: z.boolean(),
  unavailableItems: z.array(UnavailableCartItemSchema),
});

export type CartValidationResult = z.infer<typeof CartValidationResultSchema>;

export const CheckoutSummarySchema = z.object({
  cart: CartSchema,
  inventoryAvailable: z.boolean(),
  estimatedTax: z.number().int(),
  estimatedTotal: z.number().int(),
  availablePaymentMethods: z.array(z.string()).optional(),
});

export type CheckoutSummary = z.infer<typeof CheckoutSummarySchema>;

export const CheckoutSummaryResponseSchema = z.object({
  success: z.boolean(),
  data: CheckoutSummarySchema,
});

export type CheckoutSummaryResponse = z.infer<typeof CheckoutSummaryResponseSchema>;

export const AddToCartRequestSchema = z.object({
  ticketTypeId: z.string(),
  quantity: z.number().int().positive().max(10),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  visitorDetails: z.array(VisitorDetailsSchema).optional(),
});

export type AddToCartRequest = z.infer<typeof AddToCartRequestSchema>;

export const UpdateCartItemRequestSchema = z.object({
  quantity: z.number().int().positive().max(10),
  visitorDetails: z.array(VisitorDetailsSchema).optional(),
});

export type UpdateCartItemRequest = z.infer<typeof UpdateCartItemRequestSchema>;

export const ApplyCouponRequestSchema = z.object({
  couponCode: z.string().max(50),
});

// ============================================================================
// Cart — Attraction Attachment Request
// ============================================================================

export const AttractionSelectionRequestSchema = z.object({
  ticketTypeId: z.string().uuid(),
  quantity: z.number().int().min(1).max(10),
  slotId: z.string().optional(),
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
  fromBundleInclusion: z.boolean().optional(),
  /** Which bundle line this row came from, when `fromBundleInclusion` is true. */
  bundleItemKey: BundleItemKeySchema.optional(),
  /** Which visitor of the owning ENTRY line this row's slot belongs to (0-based). */
  parentEntryUnitIndex: z.number().int().nonnegative().optional(),
  /** Which ENTRY line of the bundle this row belongs to. */
  parentEntryItemKey: BundleItemKeySchema.optional(),
});

export type AttractionSelectionRequest = z.infer<typeof AttractionSelectionRequestSchema>;

export const AddAttractionAttachmentRequestSchema = z
  .object({
    parentType: z.enum(['entry', 'bundle']),
    parentTemplateId: z.string().uuid(),
    instanceIndex: z.number().int().nonnegative(),
    instanceLabel: z.string().max(200),
    attractions: z.array(AttractionSelectionRequestSchema).min(1),
  })
  .merge(BundleParentAddressingSchema);

export type AddAttractionAttachmentRequest = z.infer<typeof AddAttractionAttachmentRequestSchema>;

// ============================================================================
// Checkout Request Schemas
// ============================================================================

export const PaymentConfigRequestSchema = z.object({
  method: z.enum([
    'midtrans_snap',
    'bank_transfer',
    'credit_card',
    'gopay',
    'qris',
    'shopeepay',
    'ovo',
    'dana',
  ]),
  bank: z.enum(['bca', 'mandiri', 'bni', 'bri', 'permata', 'cimb']).optional(),
  cardToken: z.string().optional(),
});

export type PaymentConfigRequest = z.infer<typeof PaymentConfigRequestSchema>;

export const CheckoutGuestInfoRequestSchema = z.object({
  ticketTypeId: z.string().min(1),
  name: z.string().min(1),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  dateOfBirth: z.string().optional(),
  identityNumber: z.string().optional(),
});

export type CheckoutGuestInfoRequest = z.infer<typeof CheckoutGuestInfoRequestSchema>;

export const CheckoutRequestSchema = z.object({
  sessionId: z.string().regex(/^[a-zA-Z0-9_-]{8,64}$/, { message: 'Invalid session ID format' }),
  payment: PaymentConfigRequestSchema,
  guestInfo: z.array(CheckoutGuestInfoRequestSchema).optional(),
  promoCode: z.string().optional(),
  sourceChannel: z.enum(['web', 'mobile', 'kiosk', 'reseller', 'admin']).optional(),
  idempotencyKey: z.string().optional(),
  notes: z.string().optional(),
});

export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;

export const CheckoutSummaryRequestSchema = z.object({
  sessionId: z.string().regex(/^[a-zA-Z0-9_-]{8,64}$/, { message: 'Invalid session ID format' }),
});

export type CheckoutSummaryRequest = z.infer<typeof CheckoutSummaryRequestSchema>;

export const ApplyPromoRequestSchema = z.object({
  sessionId: z.string().regex(/^[a-zA-Z0-9_-]{8,64}$/, { message: 'Invalid session ID format' }),
  promoCode: z.string().min(1),
});

export type ApplyPromoRequest = z.infer<typeof ApplyPromoRequestSchema>;

export type ApplyCouponRequest = z.infer<typeof ApplyCouponRequestSchema>;

// ============================================================================
// Orders
// ============================================================================

export const OrderItemSchema = z.object({
  id: z.string().uuid(),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: TicketCategorySchema,
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  orderBundleId: z.string().uuid().nullable().optional(),
  visitorName: z.string().nullable(),
  visitorEmail: z.string().nullable(),
  visitorPhone: z.string().nullable(),
});

export type OrderItem = z.infer<typeof OrderItemSchema>;

export const OrderBundleSchema = z.object({
  id: z.string().uuid(),
  bundleId: z.string().uuid(),
  bundleCode: z.string(),
  bundleName: z.string(),
  bundlePriceIdr: z.number().int().positive(),
  quantity: z.number().int().positive(),
  items: z.array(OrderItemSchema).optional(),
});

export type OrderBundleType = z.infer<typeof OrderBundleSchema>;

export const OrderSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  // No `.max(50)` — see `CartSchema.customerId` above. #886 widened
  // `Order.customerId` to TEXT; loosening a response schema is backward
  // compatible, so this needs no deploy ordering of its own.
  customerId: z.string().min(1),
  status: z.enum([
    'pending',
    'pending_payment',
    'paid',
    'processing',
    'partially_paid',
    'payment_failed',
    'failed',
    'fulfilled',
    'fulfillment_failed',
    'completed',
    'cancelled',
    'expired',
  ]),
  items: z.array(OrderItemSchema),
  orderBundles: z.array(OrderBundleSchema).default([]),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  couponCode: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  paymentReference: z.string().nullable(),
  visitDate: z.string(),
  sourceChannel: z.string(),
  /**
   * True for wristband addon orders, which are placed without a login (#835).
   * payment-service uses this to skip its order-ownership check — there is no
   * JWT user to compare `customerId` against. Previously inferred from a
   * `WB_` prefix on `customerId`, which no longer exists.
   */
  isGuestOrder: z.boolean().default(false),
  paidAt: z.string().datetime().nullable(),
  cancelledAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Order = z.infer<typeof OrderSchema>;

export const CreateOrderRequestSchema = z.object({
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  items: z.array(z.object({
    ticketTypeCode: z.string(),
    quantity: z.number().int().positive(),
    visitorName: z.string().max(100).optional(),
    visitorEmail: z.string().email().optional(),
    visitorPhone: z.string().max(20).optional(),
    visitorDob: z.string().optional(),
    wristbandLabel: z.string().max(50).optional(),
  })),
  bundles: z.array(z.object({
    bundleId: z.string().uuid(),
    bundleCode: z.string(),
    quantity: z.number().int().positive(),
    bundlePriceIdr: z.number().int().positive(),
    items: z.array(z.object({
      ticketTypeCode: z.string(),
      category: z.enum(['ENTRY', 'ATTRACTION']),
      quantity: z.number().int().positive(),
    })),
  })).optional(),
  // Per-instance attraction attachments for parent-child ticket creation
  attractionAttachments: z.array(OrderAttractionAttachmentSchema).optional(),
  couponCode: z.string().max(50).optional(),
  idempotencyKey: z.string().max(100).optional(),
  notes: z.string().max(500).optional(),
}).refine(
  (data) => data.items.length > 0 || (data.bundles && data.bundles.length > 0),
  { message: 'At least one item or bundle is required', path: ['items'] }
);

export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;

export const GetOrdersQuerySchema = z.object({
  status: z.string().optional(),
  customerId: z.string().optional(),
  visitDate: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetOrdersQuery = z.infer<typeof GetOrdersQuerySchema>;

// ============================================================================
// Tickets
// ============================================================================

export const TicketSchema = z.object({
  id: z.string().uuid(),
  ticketNumber: z.string(),
  orderId: z.string().uuid(),
  customerId: z.string().min(1),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  qrCodeToken: z.string(),
  status: TicketStatusSchema,
  category: TicketCategorySchema,
  attractionId: z.string().uuid().nullable().optional(),
  parentTicketId: z.string().uuid().nullable().optional(),
  visitDate: z.string(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  currentLocation: z.enum(['outside_park', 'inside_park']),
  entryCount: z.number().int(),
  maxReentryCount: z.number().int(),
  lastEntryAt: z.string().datetime().nullable(),
  lastExitAt: z.string().datetime().nullable(),
  visitorName: z.string().nullable(),
  visitorEmail: z.string().nullable(),
  wristbandId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});

export type Ticket = z.infer<typeof TicketSchema>;

/** Order detail API response — includes issued tickets when loaded with tickets relation */
export const OrderDetailSchema = OrderSchema.extend({
  tickets: z.array(TicketSchema).optional(),
  metadata: z
    .object({
      bookingNumber: z.string().optional(),
      groupBookingId: z.string().optional(),
      inquiryOnly: z.boolean().optional(),
    })
    .passthrough()
    .optional(),
  orderType: z.string().optional(),
  groupOrder: z
    .object({
      id: z.string().uuid(),
      groupNumber: z.string(),
    })
    .nullable()
    .optional(),
});

export type OrderDetail = z.infer<typeof OrderDetailSchema>;

// ============================================================================
// Pagination envelope ({ items, meta }) — reuse the shared common helper
// ============================================================================

export const PaginatedOrdersSchema = PaginatedResponseSchema(OrderSchema);
export type PaginatedOrders = z.infer<typeof PaginatedOrdersSchema>;

// ============================================================================
// Orders — request/response extras (cancel, refund, retry, summary, list query)
// ============================================================================

export const CancelOrderRequestSchema = z.object({
  reason: z.string(),
});
export type CancelOrderRequest = z.infer<typeof CancelOrderRequestSchema>;

// Named OrderRefund* to avoid collision with payment's RefundResponse (a
// different shape — the payment-domain refund entity).
export const OrderRefundRequestSchema = z.object({
  reason: z.string(),
  ticketIds: z.array(z.string()).optional(),
});
export type OrderRefundRequest = z.infer<typeof OrderRefundRequestSchema>;

export const OrderRefundResponseSchema = z.object({
  refundId: z.string(),
  status: z.string(),
  estimatedAmount: z.number(),
  message: z.string(),
});
export type OrderRefundResponse = z.infer<typeof OrderRefundResponseSchema>;

export const RetryPaymentResponseSchema = z.object({
  paymentId: z.string(),
  paymentUrl: z.string(),
  snapToken: z.string().optional(),
  clientKey: z.string().optional(),
  paymentPlatform: z.string(),
  expiresAt: z.string(),
});
export type RetryPaymentResponse = z.infer<typeof RetryPaymentResponseSchema>;

export const OrderSummarySchema = z.object({
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  items: z.array(
    z.object({
      ticketTypeCode: z.string(),
      ticketTypeName: z.string(),
      quantity: z.number().int(),
      unitPriceIdr: z.number().int(),
      totalPriceIdr: z.number().int(),
    }),
  ),
  couponApplied: z
    .object({
      code: z.string(),
      discountAmount: z.number(),
    })
    .optional(),
});
export type OrderSummary = z.infer<typeof OrderSummarySchema>;

function toIsoDateTime(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  return typeof value === 'string' ? value : value.toISOString();
}

function toVisitDateString(value: Date | string): string {
  if (typeof value === 'string') {
    return value.includes('T') ? value.split('T')[0]! : value;
  }
  return value.toISOString().split('T')[0]!;
}

/** Map a persisted ticket row to the public API contract. */
export function mapTicketForContract(ticket: Record<string, unknown>): Ticket {
  return TicketSchema.parse({
    id: ticket.id,
    ticketNumber: ticket.ticketNumber,
    orderId: ticket.orderId,
    customerId: ticket.customerId,
    ticketTypeId: ticket.ticketTypeId,
    ticketTypeCode: ticket.ticketTypeCode,
    qrCodeToken: ticket.qrCodeToken,
    status: ticket.status,
    category: ticket.category,
    attractionId: ticket.attractionId ?? null,
    parentTicketId: ticket.parentTicketId ?? null,
    visitDate: toVisitDateString(ticket.visitDate as Date | string),
    validFrom: toIsoDateTime(ticket.validFrom as Date | string),
    validUntil: toIsoDateTime(ticket.validUntil as Date | string),
    currentLocation: ticket.currentLocation,
    entryCount: ticket.entryCount,
    maxReentryCount: ticket.maxReentryCount,
    lastEntryAt: toIsoDateTime(ticket.lastEntryAt as Date | string | null),
    lastExitAt: toIsoDateTime(ticket.lastExitAt as Date | string | null),
    visitorName: ticket.visitorName ?? null,
    visitorEmail: ticket.visitorEmail ?? null,
    wristbandId: ticket.wristbandId ?? null,
    createdAt: toIsoDateTime(ticket.createdAt as Date | string),
  });
}

/** Normalize a serialized order payload to {@link OrderDetail}. */
export function toOrderDetail(order: Record<string, unknown>): OrderDetail {
  const tickets = Array.isArray(order.tickets)
    ? order.tickets.map((ticket) => mapTicketForContract(ticket as Record<string, unknown>))
    : undefined;

  return OrderDetailSchema.parse({
    ...order,
    visitDate: toVisitDateString(order.visitDate as Date | string),
    paidAt: toIsoDateTime(order.paidAt as Date | string | null),
    cancelledAt: toIsoDateTime(order.cancelledAt as Date | string | null),
    createdAt: toIsoDateTime(order.createdAt as Date | string),
    updatedAt: toIsoDateTime(order.updatedAt as Date | string),
    tickets,
  });
}

export const GetTicketsQuerySchema = z.object({
  status: z.string().optional(),
  visitDate: z.string().optional(),
  orderId: z.string().uuid().optional(),
  customerId: z.string().optional(),
  ticketTypeCode: z.string().optional(),
  category: TicketCategorySchema.optional(),
  parentTicketId: z.string().uuid().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetTicketsQuery = z.infer<typeof GetTicketsQuerySchema>;

export const TransferTicketRequestSchema = z.object({
  recipientEmail: z.string().email(),
  recipientName: z.string().max(100),
  recipientPhone: z.string().max(20).optional(),
  transferReason: z.string().max(500).optional(),
});

export type TransferTicketRequest = z.infer<typeof TransferTicketRequestSchema>;

// ============================================================================
// Wristband Attraction Lookup (New -- Ticket Bundling Sprint 3)
// ============================================================================

export const WristbandAttractionTicketSchema = z.object({
  ticketId: z.string().uuid(),
  ticketNumber: z.string(),
  attractionName: z.string(),
  attractionCode: z.string(),
  status: TicketStatusSchema,
  firstUsedAt: z.string().datetime().nullable(),
});

export type WristbandAttractionTicket = z.infer<typeof WristbandAttractionTicketSchema>;

export const WristbandAttractionsResponseSchema = z.object({
  entryTicket: z.object({
    ticketId: z.string().uuid(),
    ticketNumber: z.string(),
    ticketTypeName: z.string(),
    status: TicketStatusSchema,
    visitDate: z.string(),
    visitorName: z.string().nullable(),
  }),
  attractionTickets: z.array(WristbandAttractionTicketSchema),
  summary: z.object({
    totalAttractionTickets: z.number().int().nonnegative(),
    activeCount: z.number().int().nonnegative(),
    usedCount: z.number().int().nonnegative(),
    expiredCount: z.number().int().nonnegative(),
  }),
});

export type WristbandAttractionsResponse = z.infer<typeof WristbandAttractionsResponseSchema>;

// ============================================================================
// In-Park Purchase (New -- Ticket Bundling Sprint 3)
// ============================================================================

export const InParkPurchaseRequestSchema = z.object({
  attractionTicketTypeId: z.string().uuid(),
  quantity: z.number().int().positive().max(10),
  paymentMethod: z.enum([
    'credit_balance',
    'ewallet_gopay',
    'ewallet_ovo',
    'ewallet_dana',
    'ewallet_shopeepay',
    'qris',
    'edc',
  ]),
  paymentReference: z.string().optional(),
});

export type InParkPurchaseRequest = z.infer<typeof InParkPurchaseRequestSchema>;

export const InParkPurchaseResponseSchema = z.object({
  ticketId: z.string().uuid(),
  attractionId: z.string().uuid(),
  attractionName: z.string(),
  status: z.literal('ACTIVE'),
  parentTicketId: z.string().uuid(),
  orderItemId: z.string().uuid(),
  pricePaidIdr: z.number().int(),
});

export type InParkPurchaseResponse = z.infer<typeof InParkPurchaseResponseSchema>;

export const ChildTicketSchema = z.object({
  ticketId: z.string().uuid(),
  ticketNumber: z.string(),
  attractionName: z.string(),
  attractionCode: z.string(),
  status: z.string(),
  category: z.string(),
  firstUsedAt: z.string().datetime().nullable(),
});

export type ChildTicket = z.infer<typeof ChildTicketSchema>;

// ============================================================================
// Coupons
// ============================================================================

export const CouponSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  maxDiscountIdr: z.number().int().nullable(),
  minPurchaseIdr: z.number().int(),
  usageLimit: z.number().int().nullable(),
  usageCount: z.number().int(),
  perCustomerLimit: z.number().int(),
  isStackable: z.boolean(),
  requiresLogin: z.boolean(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  isActive: z.boolean(),
});

export type Coupon = z.infer<typeof CouponSchema>;

/**
 * Coupon discount type used by the validate/apply flow.
 * NOTE: distinct from the admin `CouponSchema.discountType` (`percentage | fixed`)
 * — the validation API uses `fixed_amount`.
 */
export const CouponDiscountTypeSchema = z.enum(['percentage', 'fixed_amount']);
export type CouponDiscountType = z.infer<typeof CouponDiscountTypeSchema>;

/** Programmatic error codes returned by POST /coupons/validate (canonical: ValidateCouponResponseDto). */
export const CouponErrorCodeSchema = z.enum([
  'COUPON_NOT_FOUND',
  'COUPON_EXPIRED',
  'COUPON_NOT_YET_ACTIVE',
  'COUPON_INACTIVE',
  'COUPON_USAGE_LIMIT_REACHED',
  'COUPON_PER_CUSTOMER_LIMIT_REACHED',
  'COUPON_MIN_ORDER_NOT_MET',
  // A ceiling on the cart, not a floor. Carried by legacy discounts as
  // `conditions.maxPurchaseAmount` — a promo meant for small baskets that must not
  // apply to a large one.
  'COUPON_MAX_ORDER_EXCEEDED',
  'COUPON_INVALID_TICKET_TYPE',
  'COUPON_REQUIRES_LOGIN',
  'COUPON_ALREADY_USED',
]);
export type CouponErrorCode = z.infer<typeof CouponErrorCodeSchema>;

export const ValidateCouponRequestSchema = z.object({
  code: z.string().min(1).max(50),
  subtotalIdr: z.number().min(0),
  visitDate: z.string().optional(),
  ticketTypeCodes: z.array(z.string()).optional(),
  customerId: z.string().optional(),
});

export type ValidateCouponRequest = z.infer<typeof ValidateCouponRequestSchema>;

export const ValidateCouponResponseSchema = z.object({
  valid: z.boolean(),
  code: z.string(),
  discountType: CouponDiscountTypeSchema,
  discountValue: z.number(),
  discountAmountIdr: z.number(),
  maxDiscountIdr: z.number().optional(),
  minOrderIdr: z.number().optional(),
  description: z.string().optional(),
  expiresAt: z.string().optional(),
  errorMessage: z.string().optional(),
  errorCode: CouponErrorCodeSchema.optional(),
});

export type ValidateCouponResponse = z.infer<typeof ValidateCouponResponseSchema>;

/** Applied-coupon state surfaced in checkout UI. */
export const AppliedCouponSchema = z.object({
  code: z.string(),
  discountType: CouponDiscountTypeSchema,
  discountValue: z.number(),
  discountAmountIdr: z.number(),
  description: z.string().optional(),
});

export type AppliedCoupon = z.infer<typeof AppliedCouponSchema>;

// ============================================================================
// Admin Coupon CRUD
// ============================================================================

export const AdminCouponDiscountTypeSchema = z.enum(['PERCENTAGE', 'FIXED_AMOUNT', 'BOGO', 'BUNDLE_DISCOUNT']);
export type AdminCouponDiscountType = z.infer<typeof AdminCouponDiscountTypeSchema>;

/** Serialized coupon shape returned by admin-coupons.service.ts serializeCoupon(). */
export const AdminCouponSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  discountType: z.string(),
  discountValue: z.number(),
  maxDiscountIdr: z.number().int().nullable(),
  minPurchaseIdr: z.number().int(),
  applicableTicketTypes: z.array(z.string()),
  excludedTicketTypes: z.array(z.string()),
  usageLimit: z.number().int().nullable(),
  usageCount: z.number().int(),
  perCustomerLimit: z.number().int().nullable(),
  isStackable: z.boolean(),
  requiresLogin: z.boolean(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  isActive: z.boolean(),
  createdBy: z.string().nullable(),
  channel: z.array(z.string()),
  validVisitDates: z.array(z.string()),
  batchId: z.string().nullable(),
  metadata: z.unknown(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type AdminCoupon = z.infer<typeof AdminCouponSchema>;

export const AdminCouponDetailSchema = AdminCouponSchema.extend({
  usageSummary: z.object({
    totalUsages: z.number().int(),
    totalDiscountIdr: z.number().int(),
    uniqueCustomers: z.number().int(),
    usageLast7Days: z.number().int(),
    usageLast30Days: z.number().int(),
  }).optional(),
});
export type AdminCouponDetail = z.infer<typeof AdminCouponDetailSchema>;

export const CreateCouponRequestSchema = z.object({
  code: z.string().max(50),
  name: z.string().max(100),
  description: z.string().max(500).optional(),
  discountType: AdminCouponDiscountTypeSchema,
  discountValue: z.number().min(0),
  maxDiscountIdr: z.number().int().optional(),
  minPurchaseIdr: z.number().int().optional(),
  applicableTicketTypes: z.array(z.string()).optional(),
  excludedTicketTypes: z.array(z.string()).optional(),
  usageLimit: z.number().int().optional(),
  perCustomerLimit: z.number().int().optional(),
  isStackable: z.boolean().optional(),
  requiresLogin: z.boolean().optional(),
  validFrom: z.string(),
  validUntil: z.string(),
  isActive: z.boolean().optional(),
  channel: z.array(z.string()).optional(),
  validVisitDates: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateCouponRequest = z.infer<typeof CreateCouponRequestSchema>;

export const UpdateCouponRequestSchema = CreateCouponRequestSchema.omit({ code: true }).partial();
export type UpdateCouponRequest = z.infer<typeof UpdateCouponRequestSchema>;

export const ListCouponsQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  isActive: z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]).optional(),
  discountType: z.string().optional(),
  search: z.string().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});
export type ListCouponsQuery = z.infer<typeof ListCouponsQuerySchema>;

export const BatchGenerateCouponsRequestSchema = z.object({
  name: z.string().max(100),
  prefix: z.string().max(20),
  pattern: z.enum(['random', 'sequential']),
  count: z.number().int().min(1).max(500),
  template: z.record(z.unknown()),
});
export type BatchGenerateCouponsRequest = z.infer<typeof BatchGenerateCouponsRequestSchema>;

export const BatchGenerateCouponsResponseSchema = z.object({
  batchId: z.string().uuid(),
  name: z.string(),
  totalGenerated: z.number().int(),
  sampleCodes: z.array(z.string()),
});
export type BatchGenerateCouponsResponse = z.infer<typeof BatchGenerateCouponsResponseSchema>;

export const CouponUsageQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  granularity: z.enum(['day', 'week', 'month']).optional(),
});
export type CouponUsageQuery = z.infer<typeof CouponUsageQuerySchema>;

export const CouponUsageAnalyticsSchema = z.object({
  couponId: z.string().uuid(),
  couponCode: z.string(),
  period: z.object({ from: z.string(), to: z.string() }),
  summary: z.object({
    totalUsages: z.number().int(),
    totalDiscountIdr: z.number().int(),
    averageDiscountIdr: z.number().int(),
    uniqueCustomers: z.number().int(),
    averageOrderValueIdr: z.number().int(),
  }),
  timeSeries: z.array(z.object({
    date: z.string(),
    usages: z.number().int(),
    discountIdr: z.number().int(),
  })),
  topTicketTypes: z.array(z.object({
    ticketTypeCode: z.string(),
    count: z.number().int(),
  })),
});
export type CouponUsageAnalytics = z.infer<typeof CouponUsageAnalyticsSchema>;

export const ApplyCouponResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  couponCode: z.string(),
  discountAmountIdr: z.number().int(),
  newTotalIdr: z.number().int(),
});
export type ApplyCouponResponse = z.infer<typeof ApplyCouponResponseSchema>;

// ============================================================================
// Retry Payment
// ============================================================================

export const RetryPaymentBodySchema = z.object({
  successRedirectUrl: z.string().url(),
  failureRedirectUrl: z.string().url(),
});

export type RetryPaymentBody = z.infer<typeof RetryPaymentBodySchema>;

// ============================================================================
// Attraction Admin — Change Status
// ============================================================================

export const ChangeAttractionStatusRequestSchema = z.object({
  status: AttractionStatusSchema,
});

export type ChangeAttractionStatusRequest = z.infer<typeof ChangeAttractionStatusRequestSchema>;

// ============================================================================
// Attraction Credit Bundles & Credit Config
// ============================================================================

export const AttractionCreditConfigSchema = z.object({
  attractionId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  creditCost: z.number().int().nonnegative(),
  accessType: AttractionAccessTypeSchema,
  isActive: z.boolean(),
  updatedAt: z.string(),
});
export type AttractionCreditConfig = z.infer<typeof AttractionCreditConfigSchema>;

export const AttractionCreditBundleSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  credits: z.number().int(),
  priceIdr: z.number().int(),
  bonusCredits: z.number().int(),
  totalCredits: z.number().int(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  metadata: z.unknown(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AttractionCreditBundle = z.infer<typeof AttractionCreditBundleSchema>;

export const CreateCreditBundleSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  nameId: z.string().min(1).max(100),
  description: z.string().optional(),
  credits: z.number().int().positive(),
  priceIdr: z.number().int().positive(),
  bonusCredits: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateCreditBundleInput = z.infer<typeof CreateCreditBundleSchema>;

export const UpdateCreditBundleSchema = CreateCreditBundleSchema.omit({ code: true }).partial();
export type UpdateCreditBundleInput = z.infer<typeof UpdateCreditBundleSchema>;

export const UpdateCreditConfigSchema = z.object({
  creditCost: z.number().int().min(0),
  accessType: AttractionAccessTypeSchema,
});
export type UpdateCreditConfigInput = z.infer<typeof UpdateCreditConfigSchema>;

// ============================================================================
// Attraction Schedule Admin
// ============================================================================

export const ScheduleCalendarQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/).optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type ScheduleCalendarQuery = z.infer<typeof ScheduleCalendarQuerySchema>;

export const CloseAttractionDateSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(1).max(200),
});
export type CloseAttractionDateInput = z.infer<typeof CloseAttractionDateSchema>;

// ============================================================================
// Ticket Date-Range Availability
// ============================================================================

export const GetDateRangeAvailabilityQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  ticketTypeCode: z.string().optional(),
});
export type GetDateRangeAvailabilityQuery = z.infer<typeof GetDateRangeAvailabilityQuerySchema>;

export const DateAvailabilitySummarySchema = z.object({
  date: z.string(),
  status: z.enum(['available', 'limited', 'sold_out', 'blocked']),
  lowestPriceIdr: z.number().int(),
  hasBlocked: z.boolean(),
  capacityPercentage: z.number().int(),
});
export type DateAvailabilitySummary = z.infer<typeof DateAvailabilitySummarySchema>;

// ============================================================================
// Storefront Bundles
// ============================================================================

export const StorefrontBundleItemSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  ticketTypeNameId: z.string(),
  // Reuses TicketCategorySchema rather than re-declaring the enum inline; the
  // storefront must not be able to drift from the canonical vocabulary.
  category: TicketCategorySchema,
  itemKey: BundleItemKeySchema,
  parentItemKey: BundleItemKeySchema.nullable(),
  /** PER ENTRY UNIT on attraction lines — see BundleItemSchema.quantity. */
  quantity: z.number().int().positive(),
  effectiveQuantity: z.number().int().positive(),
  basePriceIdr: z.number().int(),
});

export type StorefrontBundleItem = z.infer<typeof StorefrontBundleItemSchema>;

export const StorefrontBundleSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  nameId: z.string(),
  description: z.string().nullable(),
  descriptionId: z.string().nullable(),
  bundlePriceIdr: z.number().int(),
  thumbnailUrl: z.string().nullable(),
  sortOrder: z.number().int(),
  sumOfPartsIdr: z.number().int().optional(),
  savingsIdr: z.number().int().optional(),
  savingsPercentage: z.number().optional(),
  items: z.array(StorefrontBundleItemSchema),
});

export type StorefrontBundle = z.infer<typeof StorefrontBundleSchema>;

/** Full HTTP body for GET /bundles public listing */
export const BundlesListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(StorefrontBundleSchema),
  meta: TicketTypesListMetaSchema,
});

export type BundlesListResponse = z.infer<typeof BundlesListResponseSchema>;

// ============================================================================
// Paginated Tickets
// ============================================================================

export const PaginatedTicketsSchema = PaginatedResponseSchema(TicketSchema);
export type PaginatedTickets = z.infer<typeof PaginatedTicketsSchema>;

// ============================================================================
// Storefront News
// ============================================================================

export const StorefrontNewsArticleSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  titleId: z.string(),
  excerpt: z.string().nullable(),
  excerptId: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  category: z.string().nullable(),
  isFeatured: z.boolean(),
  publishedAt: z.string().nullable(),
});

export type StorefrontNewsArticle = z.infer<typeof StorefrontNewsArticleSchema>;

// ============================================================================
// Customer Portal — Visit History
// ============================================================================

export const AttractionVisitSchema = z.object({
  attractionId: z.string(),
  attractionName: z.string(),
  visitedAt: z.string(),
});

export type AttractionVisit = z.infer<typeof AttractionVisitSchema>;

export const VisitRecordSchema = z.object({
  visitId: z.string(),
  visitDate: z.string(),
  entryTime: z.string(),
  // Nullable: customers.service.ts's getVisitHistory falls back to null for
  // QR-only visitors with no wristband (`t.wristband?.wristbandId ?? null`).
  wristbandId: z.string().nullable(),
  attractionsVisited: z.array(AttractionVisitSchema),
  pointsEarned: z.number().int(),
});

export type VisitRecord = z.infer<typeof VisitRecordSchema>;

export const VisitHistoryResponseSchema = z.object({
  visits: z.array(VisitRecordSchema),
  total: z.number().int(),
  page: z.number().int(),
  totalPages: z.number().int(),
});

export type VisitHistoryResponse = z.infer<typeof VisitHistoryResponseSchema>;

// ============================================================================
// Customer Portal — Wristband & Credit Transactions
// (Prefixed Customer* to avoid collision with wristband-service WristbandDetail
// and validation-service CreditTransaction in @autopia/api-contracts)
// ============================================================================

export const CustomerCreditTransactionSchema = z.object({
  transactionId: z.string(),
  date: z.string(),
  description: z.string(),
  amount: z.number(),
  balanceAfter: z.number(),
});

export type CustomerCreditTransaction = z.infer<typeof CustomerCreditTransactionSchema>;

export const CustomerWristbandDetailSchema = z.object({
  wristbandId: z.string(),
  activationDate: z.string(),
  // Verified against admin-wristbands.service.ts's real persisted statuses --
  // 'INACTIVE'/'EXPIRED' were never actually used; 'SUSPENDED'/'LOST'/'VOID'
  // are. A suspended/lost/void wristband is a normal successful response
  // that the old enum would have rejected under strict validation.
  status: z.enum(['ACTIVE', 'SUSPENDED', 'LOST', 'VOID']),
  creditBalance: z.number(),
  transactions: z.array(CustomerCreditTransactionSchema),
});

export type CustomerWristbandDetail = z.infer<typeof CustomerWristbandDetailSchema>;

// ============================================================================
// Customer Portal — Receipts
// ============================================================================

export const ReceiptLineItemSchema = z.object({
  name: z.string(),
  quantity: z.number().int(),
  unitPrice: z.number(),
  total: z.number(),
});

export type ReceiptLineItem = z.infer<typeof ReceiptLineItemSchema>;

export const ReceiptSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  date: z.string(),
  items: z.array(ReceiptLineItemSchema),
  subtotal: z.number(),
  tax: z.number(),
  total: z.number(),
  pdfUrl: z.string().nullable(),
  status: z.enum(['PAID', 'REFUNDED', 'CANCELLED']),
});

export type Receipt = z.infer<typeof ReceiptSchema>;

export const ReceiptsResponseSchema = z.object({
  receipts: z.array(ReceiptSchema),
  total: z.number().int(),
  page: z.number().int(),
  totalPages: z.number().int(),
});

export type ReceiptsResponse = z.infer<typeof ReceiptsResponseSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================

export const EntityCodeSchema = z.string().regex(/^[A-Z][A-Z0-9_-]{0,49}$/);

export const SessionIdSchema = z.string().regex(/^[a-zA-Z0-9_-]{8,64}$/);

// ============================================================================
// QR Resolve
// ============================================================================

export const ResolveQrRequestSchema = z.object({
  qrToken: z.string().min(1).max(512),
});
export type ResolveQrRequest = z.infer<typeof ResolveQrRequestSchema>;

export const ResolveQrResultSchema = z.object({
  type: z.enum(['wristband', 'ticket']),
  wristbandCode: z.string(),
  identity: z.object({
    name: z.string().optional(),
    email: z.string().optional(),
  }),
  visitDate: z.string(),
  parentTicketId: z.string().optional(),
  entryTicketId: z.string().optional(),
  orderId: z.string().optional(),
  ticketNumber: z.string().optional(),
});

export type ResolveQrResult = z.infer<typeof ResolveQrResultSchema>;

// ============================================================================
// Ticket QR Lookup (used by validation-service)
// ============================================================================

export const TicketQrResultSchema = z.object({
  id: z.string().uuid(),
  qrCode: z.string(),
  ticketTypeId: z.string().uuid(),
  ticketType: z.record(z.unknown()).optional(),
  category: TicketCategorySchema,
  attractionId: z.string().uuid().nullable(),
  parentTicketId: z.string().uuid().nullable(),
  visitDate: z.date(),
  customerId: z.string(),
  customerName: z.string().nullable(),
  status: TicketStatusSchema,
  maxEntries: z.number().int(),
  usedCount: z.number().int(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type TicketQrResult = z.infer<typeof TicketQrResultSchema>;

export type TicketQrSuccessResponse = SuccessResponse<TicketQrResult>;

export const RegenerateQrDataSchema = z.object({
  wristbandId: z.string(),
  qrContent: z.string(),
  hmacTruncated: z.string(),
  timestamp: z.number(),
});

export type RegenerateQrData = z.infer<typeof RegenerateQrDataSchema>;
export type RegenerateQrSuccessResponse = SuccessResponse<RegenerateQrData>;

// ============================================================================
// Customer Order History — Query
// ============================================================================

export const CustomerOrderHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  status: z.string().optional(),
  searchQuery: z.string().optional(),
  visitDate: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type CustomerOrderHistoryQuery = z.infer<typeof CustomerOrderHistoryQuerySchema>;

// ============================================================================
// Customer Order History
// ============================================================================

export const CustomerOrderHistoryItemSchema = z.object({
  id: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  quantity: z.number().int(),
  unitPriceIdr: z.number().int(),
  totalIdr: z.number().int(),
});

export type CustomerOrderHistoryItem = z.infer<typeof CustomerOrderHistoryItemSchema>;

export const CustomerOrderHistoryOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  status: z.string(),
  visitDate: z.string(),
  subtotalIdr: z.number().int(),
  discountIdr: z.number().int(),
  taxIdr: z.number().int(),
  totalIdr: z.number().int(),
  couponCode: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  itemCount: z.number().int(),
  ticketCount: z.number().int(),
  items: z.array(CustomerOrderHistoryItemSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
  paidAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
});

export type CustomerOrderHistoryOrder = z.infer<typeof CustomerOrderHistoryOrderSchema>;

export const CustomerOrderHistoryResponseSchema = z.object({
  success: z.boolean(),
  items: z.array(CustomerOrderHistoryOrderSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
    hasMore: z.boolean(),
  }),
});

export type CustomerOrderHistoryResponse = z.infer<typeof CustomerOrderHistoryResponseSchema>;

// ============================================================================
// Wallet
// ============================================================================

export const GoogleWalletUrlResponseSchema = z.object({
  saveUrl: z.string(),
});

export type GoogleWalletUrlResponse = z.infer<typeof GoogleWalletUrlResponseSchema>;

// ============================================================================
// Web Addon Order
// ============================================================================

export const CreateWebAddonOrderItemSchema = z.object({
  attractionId: z.string(),
  attractionCode: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeId: z.string(),
  quantity: z.number().int().positive(),
  slotId: z.string().optional(),
  slotDate: z.string().optional(),
  slotStartTime: z.string().optional(),
  slotEndTime: z.string().optional(),
  /**
   * @deprecated Ignored since #835 — `parentTicketId` is the addon's ownership
   * token and is resolved server-side from `wristbandCode`. Accepting a client
   * value here would let a caller parent an addon onto a stranger's entry
   * ticket. Still accepted on the wire so existing clients don't break.
   */
  parentTicketId: z.string().optional(),
});

export type CreateWebAddonOrderItem = z.infer<typeof CreateWebAddonOrderItemSchema>;

export const CreateWebAddonOrderPayloadSchema = z.object({
  idempotencyKey: z.string().min(1),
  wristbandCode: z.string(),
  visitDate: z.string(),
  locale: z.string().optional(),
  /** @deprecated Ignored since #835 — server-resolved from `wristbandCode`. */
  entryTicketId: z.string().optional(),
  orderId: z.string().optional(),
  visitorName: z.string().optional(),
  visitorEmail: z.string().optional(),
  items: z.array(CreateWebAddonOrderItemSchema).min(1),
});

export type CreateWebAddonOrderPayload = z.infer<typeof CreateWebAddonOrderPayloadSchema>;

export const CreateWebAddonOrderResultSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  totalIdr: z.number().int(),
});

export type CreateWebAddonOrderResult = z.infer<typeof CreateWebAddonOrderResultSchema>;

// ============================================================================
// Admin Orders — Request Schemas
// ============================================================================

export const AdminOrderQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).default(25),
  sortBy: z.enum(['orderDate', 'visitDate', 'total', 'status', 'orderNumber']).default('orderDate'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  status: z.string().optional(),
  paymentMethod: z.string().optional(),
  /**
   * Which POS surface sold the order — the `sourceChannel` column. Orthogonal
   * to tender: a TICKETBOX sale and an ONLINE sale can share a payment method.
   * Brought over from the monolith-wired admin build, which filtered on it.
   */
  posType: z
    .enum(['ONLINE', 'TICKETBOX', 'KIOSK', 'TRAVEL_AGENT', 'MANLESS', 'GROUP_BOOKING'])
    .optional(),
  /**
   * Coarse gateway grouping — the `gateway` column. Deliberately separate from
   * `paymentMethod`: the gateway groups over methods, and admin filters on both.
   */
  paymentGateway: z.string().optional(),
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export type AdminOrderQuery = z.infer<typeof AdminOrderQuerySchema>;

export const AddOrderNoteRequestSchema = z.object({
  note: z.string().min(1).max(1000),
});

export type AddOrderNoteRequest = z.infer<typeof AddOrderNoteRequestSchema>;

export const SubmitOrderRefundRequestSchema = z.object({
  amount: z.number().positive(),
  reason: z.string().min(1),
  notes: z.string(),
  policyChecked: z.boolean(),
});

export type SubmitOrderRefundRequest = z.infer<typeof SubmitOrderRefundRequestSchema>;

export const ReprintRequestSchema = z.object({
  type: z.enum(['RECEIPT', 'TICKET']),
  reason: z.string().max(1000).optional(),
});

export type ReprintRequest = z.infer<typeof ReprintRequestSchema>;

// ============================================================================
// Admin Orders — Response Schemas
// ============================================================================

const AdminOrderLineItemSchema = z.object({
  id: z.string().uuid(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  quantity: z.number().int(),
  unitPrice: z.number(),
  subtotal: z.number(),
});

export const AdminOrderListItemSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  orderType: z.string(),
  groupBookingNumber: z.string().nullable(),
  customerName: z.string(),
  customerEmail: z.string(),
  customerPhone: z.string(),
  orderDate: z.string(),
  visitDate: z.string(),
  status: z.string(),
  total: z.number(),
  paymentMethod: z.string(),
  ticketCount: z.number().int(),
  items: z.array(AdminOrderLineItemSchema),
});

export type AdminOrderListItem = z.infer<typeof AdminOrderListItemSchema>;

export const AdminOrdersListDataSchema = z.object({
  orders: z.array(AdminOrderListItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});

export type AdminOrdersListData = z.infer<typeof AdminOrdersListDataSchema>;

const AdminOrderDetailItemSchema = z.object({
  id: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: z.string(),
  quantity: z.number().int(),
  unitPrice: z.number(),
  subtotal: z.number(),
});

const AdminOrderDetailTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  ticketTypeCode: z.string(),
  ticketTypeName: z.string(),
  category: z.string(),
  status: z.string(),
  qrCodeToken: z.string(),
  visitorName: z.string(),
  visitorEmail: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  usedAt: z.string().optional(),
});

// AdminOrderDetailDataSchema / AdminOrderDetailData lived here and declared the
// GET /admin/orders/:id response a second time. The controller validated against
// this copy while apps/admin typed itself from AdminOrderDetailSchema in
// ./admin/orders.ts, so the two drifted with nothing to catch it — the failure
// mode Rule 2 exists to prevent, and the same one that let firstUsedAt/usedAt
// diverge (#740). Removed rather than kept in step: one endpoint, one schema.

export const AdminCancelOrderDataSchema = z.object({
  id: z.string().uuid(),
  orderNumber: z.string(),
  status: z.string(),
});

export type AdminCancelOrderData = z.infer<typeof AdminCancelOrderDataSchema>;

export const AdminAddNoteDataSchema = z.object({
  text: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
});

export type AdminAddNoteData = z.infer<typeof AdminAddNoteDataSchema>;

const AdminRefundEntrySchema = z.object({
  id: z.string(),
  orderId: z.string().optional(),
  amount: z.number(),
  reason: z.string(),
  reasonLabel: z.string(),
  notes: z.string(),
  status: z.string(),
  policyChecked: z.boolean(),
  requiresApproval: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string(),
});

export const AdminRefundsDataSchema = z.object({
  refunds: z.array(AdminRefundEntrySchema),
});

export type AdminRefundsData = z.infer<typeof AdminRefundsDataSchema>;

export const AdminSubmitRefundDataSchema = z.object({
  refund: AdminRefundEntrySchema,
});

export type AdminSubmitRefundData = z.infer<typeof AdminSubmitRefundDataSchema>;

const AdminReprintTicketSchema = z.object({
  ticketNumber: z.string(),
  ticketType: z.string().optional(),
  status: z.string(),
});

export const AdminReprintDataSchema = z.object({
  orderId: z.string(),
  type: z.enum(['RECEIPT', 'TICKET']),
  orderNumber: z.string(),
  reprintedBy: z.string(),
  reprintedAt: z.string(),
  tickets: z.array(AdminReprintTicketSchema).optional(),

  /**
   * The receipt to render, when `type` is RECEIPT.
   *
   * A RECEIPT reprint used to return only the envelope above — order number, who asked,
   * when — and no receipt. The reprint was recorded in the order's metadata either way,
   * so the audit trail said a receipt had been reprinted while the caller had been
   * handed nothing to print.
   */
  receipt: z
    .object({
      orderNumber: z.string(),
      visitDate: z.string().nullable(),
      paymentMethod: z.string().nullable(),
      lineItems: z.array(
        z.object({
          ticketTypeName: z.string(),
          quantity: z.number().int(),
          unitPriceIdr: z.number(),
          subtotalIdr: z.number(),
        }),
      ),
      subtotalIdr: z.number(),
      discountIdr: z.number(),
      taxIdr: z.number(),
      totalIdr: z.number(),
    })
    .optional(),
});

export type AdminReprintData = z.infer<typeof AdminReprintDataSchema>;

export const AdminResendTicketsDataSchema = z.object({
  ticketCount: z.number().int(),
  /**
   * True once the send is durably queued in the outbox.
   *
   * The endpoint used to return `ticketCount` alone, having sent nothing — the count
   * was the only thing in the response and it said nothing about delivery. An agent
   * resending on a customer's request read it as confirmation.
   */
  queued: z.boolean().optional(),
  /** The address the tickets were queued for, so the agent can read it back. */
  to: z.string().optional(),
});

export type AdminResendTicketsData = z.infer<typeof AdminResendTicketsDataSchema>;

/**
 * Admin retry of a failed payment-confirmation saga (#765).
 *
 * Used when the saga exhausted its step retries and compensated: the customer
 * paid, the tickets were voided, and the order was reverted to pending_payment.
 */
export const RetryFulfillmentRequestSchema = z.object({
  /** Free-text operator note, recorded in the order's audit trail. */
  reason: z.string().max(500).optional(),
});

export type RetryFulfillmentRequest = z.infer<typeof RetryFulfillmentRequestSchema>;

export const AdminRetryFulfillmentDataSchema = z.object({
  orderId: z.string().uuid(),
  orderNumber: z.string(),
  /** ID of the newly started saga instance — not the failed one. */
  sagaId: z.string(),
  /** Terminal saga status, e.g. COMPLETED / COMPENSATED / FAILED. */
  status: z.string(),
  retriedBy: z.string(),
  retriedAt: z.string(),
});

export type AdminRetryFulfillmentData = z.infer<typeof AdminRetryFulfillmentDataSchema>;

// ============================================================================
// Tickets (public) — Create + WristbandAttractionEntitlement
// ============================================================================

export const WristbandAttractionEntitlementSchema = z.object({
  id: z.string().uuid(),
  ticketNumber: z.string(),
  attractionId: z.string(),
  attractionCode: z.string(),
  attractionName: z.string().nullable(),
  ticketTypeCode: z.string(),
  slotId: z.string(),
  slotDate: z.string().nullable(),
  slotStartTime: z.string().nullable(),
  slotEndTime: z.string().nullable(),
  expiresAt: z.string(),
  usedAt: z.string().nullable(),
  createdAt: z.string(),
  visitDate: z.string(),
  status: z.enum(['USED', 'EXPIRED', 'ACTIVE']),
});
export type WristbandAttractionEntitlement = z.infer<typeof WristbandAttractionEntitlementSchema>;

export const CreateTicketSchema = z.object({
  orderId: z.string().uuid(),
  orderItemId: z.string().uuid(),
  /**
   * An identity-service `User.id` — a **cuid**, never a UUID (ADR-037
   * decision 1). Was `z.string().uuid()` until #888; `POST /api/v1/tickets`
   * rejected every real customer id with a 400, because `tickets.controller.ts`
   * runs this through a `ZodValidationPipe`. `orderId` and `orderItemId` above
   * keep `.uuid()` — those genuinely are `@default(uuid())` columns in
   * ticketing's schema. The distinction is the point: the type follows the
   * value, not the suffix `Id`.
   */
  customerId: z.string().min(1),
  ticketTypeId: z.string().uuid(),
  ticketTypeCode: z.string(),
  category: z.enum(['ENTRY', 'ATTRACTION']).optional(),
  visitDate: z.string(),
  validFrom: z.string(),
  validUntil: z.string(),
  maxReentryCount: z.number().int().min(0).max(10).optional(),
  visitorName: z.string().optional(),
  visitorEmail: z.string().email().optional(),
  visitorPhone: z.string().optional(),
});
export type CreateTicket = z.infer<typeof CreateTicketSchema>;

// ============================================================================
// Ticket Schedules
// ============================================================================

export const DailyWindowSchema = z.object({
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  slotDurationMinutes: z.number().int().min(1).optional().nullable(),
  capacityPerSlot: z.number().int().min(0).optional().nullable(),
});
export type DailyWindow = z.infer<typeof DailyWindowSchema>;

export const ScheduleRecurrenceSchema = z.object({
  frequency: z.enum(['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM']).optional(),
  interval: z.number().int().min(1).optional(),
  daysOfWeek: z.array(z.number()).optional(),
  daysOfMonth: z.array(z.number()).optional(),
  rrule: z.string().optional(),
  dates: z.array(z.string()).optional(),
});
export type ScheduleRecurrence = z.infer<typeof ScheduleRecurrenceSchema>;

export const CreateTicketScheduleSchema = z.object({
  name: z.string().max(200),
  description: z.string().optional(),
  scheduleType: z.enum(['OPEN', 'RECURRING', 'DATE_LIST']),
  recurrence: ScheduleRecurrenceSchema.optional(),
  dailyWindows: z.array(DailyWindowSchema),
  reducedHoursWindows: z.array(DailyWindowSchema).optional(),
  alternateHoursWindows: z.array(DailyWindowSchema).optional(),
  blackoutDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
  blackoutBehavior: z.enum(['CLOSED', 'REDUCED_HOURS', 'ALTERNATE_HOURS']).optional(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type CreateTicketSchedule = z.infer<typeof CreateTicketScheduleSchema>;

export const UpdateTicketScheduleSchema = CreateTicketScheduleSchema.partial();
export type UpdateTicketSchedule = z.infer<typeof UpdateTicketScheduleSchema>;

export const QueryTicketScheduleSchema = z.object({
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
  scheduleType: z.enum(['OPEN', 'RECURRING', 'DATE_LIST']).optional(),
  // Filters to schedules attached to this attraction via
  // AttractionScheduleConfig (the priority-ordered many-to-many, not the
  // Attraction.scheduleId fallback FK) -- lets the admin UI warn when an
  // override already exists there (PR #759 review).
  attractionId: z.string().uuid().optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20).optional(),
});
export type QueryTicketSchedule = z.infer<typeof QueryTicketScheduleSchema>;

export const AttachScheduleSchema = z.object({
  attractionId: z.string().uuid(),
  priority: z.number().int().min(0).optional(),
  validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type AttachSchedule = z.infer<typeof AttachScheduleSchema>;

export const DetachScheduleSchema = z.object({
  attractionId: z.string().uuid(),
});
export type DetachSchedule = z.infer<typeof DetachScheduleSchema>;

export const TicketScheduleResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  scheduleType: z.enum(['OPEN', 'RECURRING', 'DATE_LIST']),
  recurrence: z.unknown(),
  dailyWindows: z.unknown(),
  reducedHoursWindows: z.unknown(),
  alternateHoursWindows: z.unknown(),
  blackoutDates: z.array(z.string()),
  blackoutBehavior: z.enum(['CLOSED', 'REDUCED_HOURS', 'ALTERNATE_HOURS']),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  timezone: z.string(),
  deletedAt: z.string().nullable(),
  deletedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TicketScheduleResponse = z.infer<typeof TicketScheduleResponseSchema>;

const AttractionScheduleConfigSchema = z.object({
  id: z.string().uuid(),
  attractionId: z.string().uuid(),
  priority: z.number().int(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  attraction: z.object({
    id: z.string().uuid(),
    name: z.string(),
    nameId: z.string(),
    code: z.string(),
  }),
});

export const TicketScheduleDetailResponseSchema = TicketScheduleResponseSchema.extend({
  attractionConfigs: z.array(AttractionScheduleConfigSchema),
});
export type TicketScheduleDetailResponse = z.infer<typeof TicketScheduleDetailResponseSchema>;

export const ScheduleListResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(TicketScheduleResponseSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
});
export type ScheduleListResponse = z.infer<typeof ScheduleListResponseSchema>;

export const RegenerateScheduleQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dryRun: BooleanFromString.default(false).optional(),
});
export type RegenerateScheduleQuery = z.infer<typeof RegenerateScheduleQuerySchema>;

// ============================================================================
// Group Bookings (schemas in separate file for size)
// ============================================================================

export * from './groups';
export * from './attraction-attachment';
export * from './addon-transfers';
export * from './kiosk-inter-service';
export * from './news';
export * from './bundle-purchases';
export * from './bulk';
export * from './discount-eval';

